import React from "react";

export const CORE_OPTIONS = ["Paper", "Plastic", "Coreless"];
export const COLOR_OPTIONS = ["Transparent", "Black", "White", "Blue", "Green", "Red", "Yellow"];
export const GRADE_OPTIONS = ["Machine", "Manual"];
export const BOX_OPTIONS = ["Carton", "Shrink Wrap", "Pallet", "Loose"];

// LLDPE stretch film density (g/cm³ ≈ kg/L)
export const LLDPE_DENSITY = 0.92;

// Pure film mass (kg) for one roll given dims in microns, mm and metres.
// Volume(m³) = (µ × 1e-6) × (mm × 1e-3) × m → kg = Volume × density × 1000
// Simplifies to: weight_kg = 9.2e-7 × thickness_µ × width_mm × length_m
export function calcRollWeightKg(thicknessMicron, widthMm, lengthM, density = LLDPE_DENSITY) {
  const k = density * 1e-6; // 9.2e-7 when density = 0.92
  return +(k * (thicknessMicron || 0) * (widthMm || 0) * (lengthM || 0)).toFixed(3);
}
export function calcRollLengthM(thicknessMicron, widthMm, weightKg, density = LLDPE_DENSITY) {
  const denom = density * 1e-6 * (thicknessMicron || 0) * (widthMm || 0);
  if (!denom) return 0;
  return +((weightKg || 0) / denom).toFixed(1);
}

const Field = ({ label, children, testId, hint }) => (
  <div>
    <label className="text-[10px] font-semibold text-zinc-900 uppercase tracking-wider mb-1 block">{label}</label>
    <div data-testid={testId}>{children}</div>
    {hint && <div className="text-[9px] text-zinc-500 mt-0.5 font-mono">{hint}</div>}
  </div>
);

const inputCls =
  "w-full h-9 px-2.5 text-sm border border-zinc-300 bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 focus:border-orange-600 font-mono";

export default function SpecFields({ spec, setSpec, prefix = "spec" }) {
  const set = (k, v) => setSpec({ ...spec, [k]: v });

  // Auto-compute carton net (roll_weight_kg) on change
  const recalcCarton = (next) => {
    const wpr = next.weight_per_roll_kg === "" ? 0 : (next.weight_per_roll_kg || 0);
    const rpc = next.rolls_per_carton === "" ? 1 : (next.rolls_per_carton || 1);
    return { ...next, roll_weight_kg: +(wpr * rpc).toFixed(3) };
  };

  // Auto-balance weight↔length using LLDPE density when the user fills the OTHER field.
  const onThickness = (raw) => {
    const v = raw === "" ? 0 : parseFloat(raw) || 0;
    const next = { ...spec, thickness_micron: v };
    // If both width and length are set → recompute weight; else if weight set → recompute length
    if (next.width_mm && next.roll_length_m) {
      next.weight_per_roll_kg = calcRollWeightKg(v, next.width_mm, next.roll_length_m);
    } else if (next.width_mm && next.weight_per_roll_kg) {
      next.roll_length_m = calcRollLengthM(v, next.width_mm, next.weight_per_roll_kg);
    }
    setSpec(recalcCarton(next));
  };
  const onWidth = (raw) => {
    const v = raw === "" ? 0 : parseFloat(raw) || 0;
    const next = { ...spec, width_mm: v };
    if (next.thickness_micron && next.roll_length_m) {
      next.weight_per_roll_kg = calcRollWeightKg(next.thickness_micron, v, next.roll_length_m);
    } else if (next.thickness_micron && next.weight_per_roll_kg) {
      next.roll_length_m = calcRollLengthM(next.thickness_micron, v, next.weight_per_roll_kg);
    }
    setSpec(recalcCarton(next));
  };
  const onWeightPerRoll = (raw) => {
    const v = raw === "" ? "" : (parseFloat(raw) || 0);
    const next = { ...spec, weight_per_roll_kg: v };
    if (next.thickness_micron && next.width_mm && raw !== "") {
      next.roll_length_m = calcRollLengthM(next.thickness_micron, next.width_mm, v);
    }
    setSpec(recalcCarton(next));
  };
  const onLength = (raw) => {
    const v = raw === "" ? 0 : (parseFloat(raw) || 0);
    const next = { ...spec, roll_length_m: v };
    if (next.thickness_micron && next.width_mm && v > 0) {
      next.weight_per_roll_kg = calcRollWeightKg(next.thickness_micron, next.width_mm, v);
    }
    setSpec(recalcCarton(next));
  };
  const onRpc = (raw) => {
    const v = raw === "" ? "" : parseInt(raw);
    setSpec(recalcCarton({ ...spec, rolls_per_carton: v }));
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Thickness (µ)" testId={`${prefix}-thickness`}>
          <input type="number" step="0.1" className={inputCls} value={spec.thickness_micron} onChange={(e) => onThickness(e.target.value)} />
        </Field>
        <Field label="Width (mm)" testId={`${prefix}-width`}>
          <input type="number" step="1" className={inputCls} value={spec.width_mm} onChange={(e) => onWidth(e.target.value)} />
        </Field>
        <Field label="Weight / Roll (kg)" testId={`${prefix}-weight-per-roll`} hint="auto if length set">
          <input type="number" step="0.001" className={inputCls} value={spec.weight_per_roll_kg ?? ""} onChange={(e) => onWeightPerRoll(e.target.value)} />
        </Field>
        <Field label="Length / Roll (m)" testId={`${prefix}-length`} hint="auto if weight set">
          <input type="number" step="1" className={inputCls} value={spec.roll_length_m} onChange={(e) => onLength(e.target.value)} />
        </Field>
        <Field label="Rolls / Carton" testId={`${prefix}-rolls-per-carton`}>
          <input type="number" step="1" min="1" className={inputCls} value={spec.rolls_per_carton ?? ""} onChange={(e) => onRpc(e.target.value)} />
        </Field>
        <Field label="Color" testId={`${prefix}-color`}>
          <select className={inputCls} value={spec.color} onChange={(e) => set("color", e.target.value)}>
            {COLOR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Grade" testId={`${prefix}-grade`}>
          <select className={inputCls} value={spec.grade} onChange={(e) => set("grade", e.target.value)}>
            {GRADE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Net / Carton (auto)" testId={`${prefix}-net-carton`}>
          <input type="number" step="0.001" className={`${inputCls} bg-orange-50 text-orange-700 font-semibold`} value={spec.roll_weight_kg || 0} readOnly />
        </Field>
      </div>
      <div className="text-[10px] font-mono text-zinc-500 mt-2">
        LLDPE auto-calc: <span className="text-orange-700">weight = 0.92 × thickness(µ) × width(mm) × length(m) × 10⁻⁶ kg</span>. Fill any 3 of {`{thickness, width, weight, length}`} — the 4th fills automatically.
      </div>
    </>
  );
}

export const defaultSpec = {
  thickness_micron: 23,
  width_mm: 500,
  roll_weight_kg: 2.5,
  roll_length_m: 300,
  weight_per_roll_kg: 2.5,
  rolls_per_carton: 1,
  core_type: "Paper",
  color: "Transparent",
  grade: "Machine",
  box_type: "Carton",
  net_weight_kg: 0,
  core_weight_kg: 0,
  box_weight_kg: 0,
  gross_weight_kg: 0,
};

export function formatSpec(s) {
  if (!s) return "—";
  return `${s.thickness_micron}µ · ${s.width_mm}mm · ${s.roll_weight_kg}kg · ${s.color} · ${s.grade}`;
}
